import type { SupplierOrderDto } from '../../../dto/supplierOrder';
import type { SupplierOrderTransportDto } from '../../../dto/transport';
import { formatCurrency } from '../../../utils/currency';
import { formatUnit } from '../../../utils/units';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';

export interface SupplierOrderCostRow {
  id: string;
  name: string;
  quantityLabel: string;
  pricingQuantityLabel: string | null;
  unitCostLabel: string | null;
  /** Value of the goods in PLN as the backend priced it; null = no price. */
  goodsPLN: number | null;
  /** Share of the truck freights in PLN; null = no truck carries this item with a known freight. */
  freightPLN: number | null;
  /** Goods + freight share; null when the goods have no price. */
  totalPLN: number | null;
  /** Cost of one unit of the item (total / quantity); null when there is no total. */
  perUnitPLN: number | null;
  unitLabel: string;
}

export interface SupplierOrderCostSummary {
  rows: SupplierOrderCostRow[];
  goodsPLN: number | null;
  /** Sum of this ZD's shares of its trucks; null = no freight known yet. */
  freightPLN: number | null;
  /** Customs and other landed costs entered on the ZD. */
  customsPLN: number;
  totalPLN: number | null;
  /** Some item has no purchase price (the total is partial). */
  incompletePrices: boolean;
}

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * Figures of the „Koszt dostawy (partia)” card, only from what the backend
 * returned for this ZD (item `totalCostPLN`, truck `freight` per ZD and per
 * item, `landedCostAmountPLN`). Missing is never zero: an unknown freight stays
 * null and the total says it is partial.
 */
export function buildSupplierOrderCostSummary(
  order: SupplierOrderDto,
  transports: SupplierOrderTransportDto[] | null,
  language = 'pl',
): SupplierOrderCostSummary {
  const freights = (transports ?? []).map((transport) => transport.freight).filter((freight) => Boolean(freight));
  const knownFreights = freights.filter((freight) => finite(freight?.amountPLN));
  const freightPLN = knownFreights.length ? round2(knownFreights.reduce((sum, freight) => sum + (freight!.amountPLN as number), 0)) : null;

  const rows: SupplierOrderCostRow[] = (order.items ?? []).map((item) => {
    const lines = freights.flatMap((freight) => freight!.lines.filter((line) => line.id === item.id && finite(line.amountPLN)));
    const itemFreight = lines.length ? round2(lines.reduce((sum, line) => sum + (line.amountPLN as number), 0)) : null;
    const goods = finite(item.totalCostPLN) ? item.totalCostPLN : null;
    const unit = formatUnit(item.unit || 'pcs', language);
    // The purchase price is per the line's pricing unit (`purchaseUnit`), which may differ from the
    // physical one (40,046 m³ bought at 10 zł / szt.): the caption names both amounts and the price
    // in its own unit; the per-unit cost on the right stays per the physical unit.
    const amount = (value: number) => formatLocalizedQuantity(value);
    const pricingUnitCode = item.purchaseUnit || item.unit || 'pcs';
    const pricingUnit = formatUnit(pricingUnitCode, language);
    const pricingDiffers = pricingUnitCode !== (item.unit || 'pcs') && finite(item.purchaseQuantity);
    return {
      id: item.id,
      name: item.name,
      quantityLabel: `${amount(item.quantity)} ${unit}`,
      pricingQuantityLabel: pricingDiffers ? `${amount(item.purchaseQuantity as number)} ${pricingUnit}` : null,
      unitCostLabel: finite(item.unitCost) && item.unitCost > 0 ? `${formatCurrency(item.unitCost, item.currency || order.currency || 'PLN')} / ${pricingUnit}` : null,
      goodsPLN: goods,
      freightPLN: itemFreight,
      totalPLN: goods === null ? null : round2(goods + (itemFreight ?? 0)),
      perUnitPLN: goods === null || !item.quantity ? null : round2((goods + (itemFreight ?? 0)) / item.quantity),
      unitLabel: unit,
    };
  });

  const pricedGoods = rows.filter((row) => row.goodsPLN !== null);
  const goodsPLN = pricedGoods.length ? round2(pricedGoods.reduce((sum, row) => sum + (row.goodsPLN as number), 0)) : null;
  const customsPLN = finite(order.landedCostAmountPLN) ? order.landedCostAmountPLN : 0;
  return {
    rows,
    goodsPLN,
    freightPLN,
    customsPLN,
    totalPLN: goodsPLN === null ? null : round2(goodsPLN + (freightPLN ?? 0) + customsPLN),
    incompletePrices: rows.some((row) => row.goodsPLN === null),
  };
}
